import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { mdiAlert, mdiCheckCircleOutline } from '@mdi/js'
import { $api } from '../../api/queryClient'
import { useIsPlatformAdmin } from '../../hooks/useAuth'
import { useLocale } from '../../contexts/LocaleContext'
import { driftedImportTypes, localizedLabel, type SyncDrift } from '../../lib/ontology-sync'
import { Alert, AlertDescription, AlertTitle } from '../ui/alert'
import { AppIcon } from '../ui/icon'

// How far the organization, and its template, are behind the shipped defaults
// (spec 2026-10-14-05). A template behind the defaults passes its drift to
// every organization built on it, and the symptom (a document type that never
// triggers a job) is invisible, so platform admins see the count.

/** The drift, fetched for platform admins only (the route refuses others). */
export function useOntologyDrift(org: string): SyncDrift | undefined {
  const isPlatformAdmin = useIsPlatformAdmin()
  const query = $api.useQuery(
    'get',
    '/api/v1/organizations/{orgCode}/types/sync/drift',
    { params: { path: { orgCode: org } } },
    { enabled: isPlatformAdmin && !!org, staleTime: 5 * 60 * 1000 },
  )
  return isPlatformAdmin ? query.data?.data : undefined
}

function DriftLine({ side, version, testId }: { side: SyncDrift['organization']; version: string; testId: string }) {
  const { t } = useTranslation('types')
  const upToDate = side.changes === 0
  return (
    <li className="flex flex-wrap items-center gap-x-2 gap-y-1" data-testid={testId}>
      <AppIcon path={upToDate ? mdiCheckCircleOutline : mdiAlert} size="sm" className={upToDate ? 'text-success' : 'text-warning'} />
      <span className="font-medium">{side.organization.name}</span>
      <span data-testid={`${testId}-count`}>
        {upToDate ? t('sync.drift.up_to_date', { version }) : t('sync.drift.behind', { count: side.changes, version })}
      </span>
      {!upToDate && (
        <Link
          to="/$org/types/sync"
          params={{ org: side.organization.code }}
          search={{ source: 'defaults' }}
          className="text-primary underline-offset-4 hover:underline"
          data-testid={`${testId}-link`}
        >
          {t('sync.drift.review')}
        </Link>
      )}
    </li>
  )
}

/** The types page notice: the organization and its template against the defaults. */
export function OntologyDriftNotice({ org }: { org: string }) {
  const { t } = useTranslation('types')
  const drift = useOntologyDrift(org)
  if (!drift) return null
  const behind = drift.organization.changes > 0 || (drift.template?.changes ?? 0) > 0
  return (
    <Alert variant={behind ? 'warning' : 'default'} data-testid="ontology-drift-notice">
      <AppIcon path={mdiAlert} size="md" />
      <AlertTitle>{t('sync.drift.title')}</AlertTitle>
      <AlertDescription>
        <ul className="mt-1 space-y-1 text-sm">
          <DriftLine side={drift.organization} version={drift.version} testId="ontology-drift-organization" />
          {drift.template && <DriftLine side={drift.template} version={drift.version} testId="ontology-drift-template" />}
        </ul>
        <p className="mt-2 text-xs text-muted-foreground">{t('sync.drift.admin_only')}</p>
      </AlertDescription>
    </Alert>
  )
}

/**
 * The import view's notice: a document type of the import differs from the
 * defaults on a key that decides its extraction jobs.
 */
export function ImportOntologyDriftAlert({ org, documentTypeCodes }: { org: string; documentTypeCodes: string[] }) {
  const { t } = useTranslation('types')
  const { locale } = useLocale()
  const drift = useOntologyDrift(org)
  const drifted = driftedImportTypes(drift, documentTypeCodes)
  if (!drift || drifted.length === 0) return null
  return (
    <Alert variant="warning" data-testid="import-ontology-drift-alert">
      <AppIcon path={mdiAlert} size="md" />
      <AlertTitle>{t('sync.drift.import_title')}</AlertTitle>
      <AlertDescription>
        <p>{t('sync.drift.import_description', { version: drift.version })}</p>
        <ul className="mt-1 list-disc pl-5 text-sm">
          {drifted.map((dt) => (
            <li key={dt.code} data-testid={`import-ontology-drift-type-${dt.code}`}>
              {localizedLabel(dt.label, locale) || dt.code} <code className="text-xs">{dt.code}</code>
              {': '}
              <span className="text-muted-foreground">{dt.keys.join(', ')}</span>
            </li>
          ))}
        </ul>
        <Link
          to="/$org/types/sync"
          params={{ org }}
          search={{ source: 'defaults', family: 'document_types', status: 'different' }}
          className="mt-2 inline-block text-primary underline-offset-4 hover:underline"
          data-testid="import-ontology-drift-link"
        >
          {t('sync.drift.review')}
        </Link>
      </AlertDescription>
    </Alert>
  )
}
